// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EllipsisSpinner from "@pace/App/Components/Common/EllipsisSpinner";
import Notify from "@pace/App/Components/Notify";
import QtyField, { QtyFieldStyles } from "@pace/App/Components/Qty/QtyField";
import updateCart from "@pace/App/Handlers/Common/UpdateCart";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { StyledBox } from "@pace/XDL/Utilities";
import React, { ChangeEvent, ReactNode, useState } from "react";
import { ResolveThunks, connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    session: state.context.session,
    addingProductToCart: state.context.addingProductToCart,
    cart: getCurrentCartState(state).value,
});

const mapDispatchToProps = {
    updateCart: makeHandlerChainAwaitable(updateCart),
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export interface OrderTermsStyles {
    textTitle?: TypographyProps;
    secondTextTitle?: TypographyProps;
    description?: TypographyProps;
    addToCartButton?: ButtonPresentationProps;
    boxContainer?: InjectableCss;
    container?: GridContainerProps;
    descriptionItemContainer?: InjectableCss;
    buttonItemContainer?: InjectableCss;
    editableQuantityTextField?: QtyFieldStyles;
}

export const orderTermsStyles: OrderTermsStyles = {};

const styles = orderTermsStyles;

const NonStockItems = ({ updateCart, session, addingProductToCart, cart }: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [productName, setProductName] = React.useState("");
    const [qty, setQty] = useState("1");
    const [errorMessage, setErrorMessage] = React.useState<React.ReactNode>("");
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [productNameError, setProductNameError] = useState<ReactNode>("");
    const [quantityError, setQuantityError] = useState<ReactNode>("");

    if (session && !session.isAuthenticated) {
        return null;
    }

    const addProductToCart = async () => {
        if (!validateData()) {
            return;
        }

        setIsSubmitted(true);
        let requestedItemsList = [];
        if (cart!.properties["requestedItems"] && cart!.properties["requestedItems"] !== "") {
            requestedItemsList = JSON.parse(cart!.properties["requestedItems"]);
        }
        requestedItemsList.push({ productName, qty });

        await updateCart({
            cart: { ...cart, properties: { requestedItems: JSON.stringify(requestedItemsList) } },
            reloadCart: true,
            onError: (e: any) => {
                setIsSubmitted(false);
                setErrorMessage(e);
            },
            onComplete: () => {
                toasterContext.addToast({ body: translate("Item added to cart"), messageType: "success" });
                setQty("1");
                setProductName("");
                setIsSubmitted(false);
            },
        } as any);
    };

    const qtyChangeHandler = (event: any) => {
        const value = event.target.value;
        let transformedValue = value;
        if (value) {
            transformedValue = value.replace(/[^0-9]/g, "");
            event.target.value = transformedValue;
        }
        setQty(transformedValue);
        validateQty(transformedValue);
    };

    const productNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const productName = event.currentTarget.value;
        setProductName(productName);
        validateProductName(productName);
    };

    const validateProductName = (partNumber: string) => {
        const partNumberValid = partNumber.trim();
        setProductNameError(partNumberValid ? "" : siteMessage("Field_Required", translate("Product Name")).toString());
        return partNumberValid;
    };

    const validateQty = (qty: string) => {
        const qtyValid = Number(qty) > 0 && qty.trim();
        setQuantityError(qtyValid ? "" : siteMessage("Field_Required", translate("Quantity")).toString());
        return qtyValid;
    };

    const validateData = (): boolean => {
        const isPartNoValid = validateProductName(productName);
        const isQtyValid = validateQty(qty);
        return !!(isPartNoValid && isQtyValid);
    };

    return (
        <StyledBox {...styles.boxContainer}>
            <StyledWrapper {...styles.container} data-test-selector="nonStockProductSelector">
                <StyledWrapper {...styles.descriptionItemContainer}>
                    <Typography {...styles.textTitle}>{siteMessage("Custom_NonStockItem_title")}</Typography>
                    <Typography {...styles.description}>{siteMessage("Custom_NonStockItem_description")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.descriptionItemContainer}>
                    <Typography {...styles.secondTextTitle}>{translate("Add Nonstocked Items")}</Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <TextField
                        type="text"
                        label={translate("Product Name")}
                        placeholder={translate("Product Name")}
                        value={productName}
                        onChange={productNameChangeHandler}
                        data-test-selector="nonStock_productName"
                        required
                        error={productNameError}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.buttonItemContainer}>
                    <QtyField
                        extendedStyles={styles.editableQuantityTextField}
                        showLabel={true}
                        value={qty}
                        onChange={qtyChangeHandler}
                        data-test-selector="nonStock_qty"
                        required
                        error={quantityError}
                    />
                    <Button
                        {...styles.addToCartButton}
                        onClick={addProductToCart}
                        disabled={isSubmitted || addingProductToCart || !productName}
                        data-test-selector="nonStock_atc"
                    >
                        <EllipsisSpinner label={translate("Add")} showSpinner={isSubmitted} />
                    </Button>
                </StyledWrapper>
                {errorMessage && (
                    <StyledWrapper>
                        <Notify type="danger" showClose={false}>
                            {errorMessage}
                        </Notify>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NonStockItems),
    definition: {
        group: "Common",
    },
};

export default widgetModule;
